import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import showToast from '@/components/ui/Toast';
import type { CustomFormField, CustomFormFieldType } from '@/services/communityService';
import {
  BADMINTON_LEVEL_TEMPLATE,
  CANONICAL_BADMINTON_LEVELS,
  isBadmintonSport,
} from '@/components/community/badminton/badmintonLevelUtils';

interface CustomFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: CustomFormField) => void;
  initialField?: CustomFormField | null;
  existingFields?: CustomFormField[];
  /** Sport category slug of the event being created/edited (e.g. badminton). */
  sportCategorySlug?: string | null;
}

const DEFAULT_ROLE_OPTIONS = [
  'Kiper (GK)',
  'Bek (DF)',
  'Gelandang (MF)',
  'Penyerang (FW)',
];

const DEFAULT_TEAM_OPTIONS = [
  'Tim A',
  'Tim B',
  'Tim C',
  'Tim D',
];

export const CustomFormModal: React.FC<CustomFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialField,
  existingFields = [],
  sportCategorySlug = null,
}) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<CustomFormFieldType>('text');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState<string[]>(['', '']);
  const [templateType, setTemplateType] = useState<
    'form_role' | 'team' | 'role' | 'player_number' | 'level' | null
  >(null);
  const [readOnlyOptions, setReadOnlyOptions] = useState(false);
  const isBadminton = isBadmintonSport(sportCategorySlug);

  // Filter out the field currently being edited
  const otherFields = existingFields.filter(
    (f) => !initialField || f.id !== initialField.id
  );

  const isRoleTemplateUsed = otherFields.some(
    (f) =>
      f.template_type === 'form_role' ||
      f.template_type === 'role' ||
      f.template === 'form_role' ||
      f.template === 'role' ||
      f.template === 'player_position' ||
      (f.title || '').toLowerCase().trim() === 'posisi pemain' ||
      (f.title || '').toLowerCase().trim() === 'posisi bermain'
  );

  const isTeamTemplateUsed = otherFields.some(
    (f) =>
      f.template_type === 'team' ||
      f.template === 'team' ||
      (f.title || '').toLowerCase().trim() === 'tim'
  );

  const isPlayerNumberTemplateUsed = otherFields.some(
    (f) =>
      f.template_type === 'player_number' ||
      f.template === 'player_number' ||
      (f.title || '').toLowerCase().trim() === 'nomor punggung' ||
      (f.title || '').toLowerCase().trim() === 'nomor jersey' ||
      (f.title || '').toLowerCase().trim() === 'no punggung' ||
      (f.title || '').toLowerCase().trim() === 'no jersey'
  );

  const isLevelTemplateUsed = otherFields.some(
    (f) =>
      f.template_type === BADMINTON_LEVEL_TEMPLATE ||
      f.template === BADMINTON_LEVEL_TEMPLATE ||
      (f.title || '').toLowerCase().trim() === 'level'
  );

  useEffect(() => {
    if (isOpen) {
      if (initialField) {
        setTitle(initialField.title || '');
        setType(initialField.type || 'text');
        setRequired(Boolean(initialField.required));
        setOptions(
          initialField.options && initialField.options.length > 0
            ? [...initialField.options]
            : ['', '']
        );
        let initType: 'form_role' | 'team' | 'player_number' | 'level' | null = null;
        if (
          initialField.template_type === 'role' ||
          initialField.template_type === 'form_role' ||
          initialField.template === 'role' ||
          initialField.template === 'form_role' ||
          initialField.template === 'player_position'
        ) {
          initType = 'form_role';
        } else if (
          initialField.template_type === 'team' ||
          initialField.template === 'team'
        ) {
          initType = 'team';
        } else if (
          initialField.template_type === 'player_number' ||
          initialField.template === 'player_number'
        ) {
          initType = 'player_number';
        } else if (
          initialField.template_type === BADMINTON_LEVEL_TEMPLATE ||
          initialField.template === BADMINTON_LEVEL_TEMPLATE
        ) {
          initType = BADMINTON_LEVEL_TEMPLATE;
        }
        setTemplateType(initType);
        setReadOnlyOptions(Boolean(initialField.read_only_options));
      } else {
        // Reset defaults
        setTitle('');
        setType('text');
        setRequired(false);
        setOptions(['', '']);
        setTemplateType(null);
        setReadOnlyOptions(false);
      }
    }
  }, [isOpen, initialField]);

  const handleApplyRoleTemplate = () => {
    if (isRoleTemplateUsed) return;
    setTitle('Posisi bermain');
    setType('option');
    setRequired(true);
    setOptions([...DEFAULT_ROLE_OPTIONS]);
    setTemplateType('form_role');
    setReadOnlyOptions(true);
  };

  const handleApplyTeamTemplate = () => {
    if (isTeamTemplateUsed) return;
    setTitle('Tim');
    setType('option');
    setRequired(true);
    setOptions([...DEFAULT_TEAM_OPTIONS]);
    setTemplateType('team');
    setReadOnlyOptions(false);
  };

  const handleApplyPlayerNumberTemplate = () => {
    if (isPlayerNumberTemplateUsed) return;
    if (templateType === 'player_number') {
      // Toggle off if already active
      setTemplateType(null);
      return;
    }
    setTitle('Nomor punggung');
    setType('number');
    setRequired(true);
    setOptions(['', '']);
    setTemplateType('player_number');
    setReadOnlyOptions(false);
  };

  const handleApplyLevelTemplate = () => {
    if (isLevelTemplateUsed) return;
    if (!isBadminton) {
      showToast.error('Khusus Badminton');
    }
    setTitle('Level');
    setType('option');
    setRequired(true);
    setOptions([...CANONICAL_BADMINTON_LEVELS]);
    setTemplateType(BADMINTON_LEVEL_TEMPLATE);
    setReadOnlyOptions(true);
  };

  const handleTypeChange = (newType: CustomFormFieldType) => {
    if (templateType === 'player_number' && newType !== 'number') {
      return;
    }
    if (templateType === BADMINTON_LEVEL_TEMPLATE && newType !== 'option') {
      return;
    }
    setType(newType);
    if (newType !== 'option') {
      // If leaving option type, clear templates that require options
      if (
        templateType === 'form_role' ||
        templateType === 'role' ||
        templateType === 'team' ||
        templateType === BADMINTON_LEVEL_TEMPLATE
      ) {
        setTemplateType(null);
        setReadOnlyOptions(false);
      }
    } else {
      if (options.length < 2) {
        setOptions(['Opsi 1', 'Opsi 2']);
      }
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const next = [...options];
    next[index] = value;
    setOptions(next);
  };

  const handleAddOption = () => {
    if (options.length >= 10) {
      showToast.error('Maksimal 10 opsi per pertanyaan');
      return;
    }
    setOptions([...options, `Opsi ${options.length + 1}`]);
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) {
      showToast.error('Minimal harus ada 2 opsi pilihan');
      return;
    }
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      showToast.error('Judul pertanyaan wajib diisi');
      return;
    }

    if ((templateType === 'form_role' || templateType === 'role') && isRoleTemplateUsed) {
      showToast.error('Template Posisi Pemain sudah digunakan pada pertanyaan lain');
      return;
    }
    if (templateType === 'team' && isTeamTemplateUsed) {
      showToast.error('Template Tim sudah digunakan pada pertanyaan lain');
      return;
    }
    if (templateType === 'player_number' && isPlayerNumberTemplateUsed) {
      showToast.error('Template Nomor Punggung sudah digunakan pada pertanyaan lain');
      return;
    }
    if (templateType === BADMINTON_LEVEL_TEMPLATE && isLevelTemplateUsed) {
      showToast.error('Template Level sudah digunakan pada pertanyaan lain');
      return;
    }
    if (templateType === BADMINTON_LEVEL_TEMPLATE && !isBadminton) {
      showToast.error('Khusus Badminton');
      // Still allow save without business effect (roles/matching handled by parent for badminton only).
    }

    let finalOptions: string[] | undefined = undefined;

    if (type === 'option' || templateType === BADMINTON_LEVEL_TEMPLATE) {
      const cleaned =
        templateType === BADMINTON_LEVEL_TEMPLATE
          ? [...CANONICAL_BADMINTON_LEVELS]
          : options.map((o) => o.trim()).filter(Boolean);
      if (cleaned.length < 2) {
        showToast.error('Minimal harus mengisi 2 opsi pilihan');
        return;
      }
      finalOptions = cleaned;
    }

    const fieldToSave: CustomFormField = {
      id: initialField?.id || `field-${Date.now()}`,
      title: trimmedTitle,
      type:
        templateType === 'player_number'
          ? 'number'
          : templateType === BADMINTON_LEVEL_TEMPLATE
            ? 'option'
            : type,
      required,
      options: finalOptions,
      template_type: templateType,
      template: templateType,
      read_only_options: templateType === BADMINTON_LEVEL_TEMPLATE ? true : readOnlyOptions,
    };

    onSave(fieldToSave);
    onClose();
  };

  const typePills: { type: CustomFormFieldType; label: string; icon: string }[] = [
    { type: 'text', label: 'Teks singkat', icon: 'ph:text-t-bold' },
    { type: 'number', label: 'Angka', icon: 'ph:hash-bold' },
    { type: 'option', label: 'Pilihan', icon: 'ph:list-bullets-bold' },
    { type: 'date', label: 'Tanggal', icon: 'ph:calendar-blank-bold' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialField ? 'Edit Pertanyaan' : 'Tambah Pertanyaan'}
      size="md"
      position="responsive"
    >
      <div className="space-y-5 py-1">
        {/* Quick Templates Section (Only when creating or if already a template) */}
        <div className="space-y-2">
          <label className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">
            PILIHAN CEPAT
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={isRoleTemplateUsed}
              onClick={handleApplyRoleTemplate}
              title={
                isRoleTemplateUsed
                  ? 'Template posisi pemain sudah digunakan'
                  : 'Gunakan template posisi pemain'
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                isRoleTemplateUsed
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                  : (templateType === 'form_role' || templateType === 'role')
                    ? 'bg-orange-50 border-[#FC480D] text-[#FC480D] shadow-xs cursor-pointer'
                    : 'bg-gray-50/80 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 cursor-pointer'
              }`}
            >
              <Icon
                icon={isRoleTemplateUsed ? 'ph:lock-simple-bold' : 'ph:lightning-bold'}
                className={`w-3.5 h-3.5 ${isRoleTemplateUsed ? 'text-gray-400' : 'text-amber-500'}`}
              />
              <span>Posisi Pemain</span>
              {isRoleTemplateUsed && (
                <span className="text-[10px] text-gray-400 font-normal">(Sudah dipakai)</span>
              )}
            </button>

            <button
              type="button"
              disabled={isTeamTemplateUsed}
              onClick={handleApplyTeamTemplate}
              title={
                isTeamTemplateUsed
                  ? 'Template tim sudah digunakan'
                  : 'Gunakan template tim'
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                isTeamTemplateUsed
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                  : templateType === 'team'
                    ? 'bg-orange-50 border-[#FC480D] text-[#FC480D] shadow-xs cursor-pointer'
                    : 'bg-gray-50/80 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 cursor-pointer'
              }`}
            >
              <Icon
                icon={isTeamTemplateUsed ? 'ph:lock-simple-bold' : 'ph:lightning-bold'}
                className={`w-3.5 h-3.5 ${isTeamTemplateUsed ? 'text-gray-400' : 'text-blue-500'}`}
              />
              <span>Tim</span>
              {isTeamTemplateUsed && (
                <span className="text-[10px] text-gray-400 font-normal">(Sudah dipakai)</span>
              )}
            </button>

            <button
              type="button"
              disabled={isLevelTemplateUsed}
              onClick={handleApplyLevelTemplate}
              title={
                isLevelTemplateUsed
                  ? 'Template level sudah digunakan'
                  : isBadminton
                    ? 'Gunakan template level badminton'
                    : 'Khusus Badminton'
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                isLevelTemplateUsed
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                  : templateType === BADMINTON_LEVEL_TEMPLATE
                    ? 'bg-orange-50 border-[#FC480D] text-[#FC480D] shadow-xs cursor-pointer'
                    : 'bg-gray-50/80 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 cursor-pointer'
              }`}
            >
              <Icon
                icon={isLevelTemplateUsed ? 'ph:lock-simple-bold' : 'ph:medal-military-bold'}
                className={`w-3.5 h-3.5 ${isLevelTemplateUsed ? 'text-gray-400' : 'text-[#FC480D]'}`}
              />
              <span>Level</span>
              {isLevelTemplateUsed && (
                <span className="text-[10px] text-gray-400 font-normal">(Sudah dipakai)</span>
              )}
            </button>

            <button
              type="button"
              disabled={isPlayerNumberTemplateUsed}
              onClick={handleApplyPlayerNumberTemplate}
              title={
                isPlayerNumberTemplateUsed
                  ? 'Template nomor punggung sudah digunakan'
                  : 'Gunakan template nomor punggung'
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                isPlayerNumberTemplateUsed
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                  : templateType === 'player_number'
                    ? 'bg-orange-50 border-[#FC480D] text-[#FC480D] shadow-xs cursor-pointer'
                    : 'bg-gray-50/80 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 cursor-pointer'
              }`}
            >
              <Icon
                icon={isPlayerNumberTemplateUsed ? 'ph:lock-simple-bold' : 'ph:t-shirt-bold'}
                className={`w-3.5 h-3.5 ${isPlayerNumberTemplateUsed ? 'text-gray-400' : 'text-emerald-500'}`}
              />
              <span>Nomor Punggung</span>
              {isPlayerNumberTemplateUsed && (
                <span className="text-[10px] text-gray-400 font-normal">(Sudah dipakai)</span>
              )}
            </button>
          </div>
        </div>

        {/* Input Question Title */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold tracking-wider text-gray-500 uppercase">
            PERTANYAAN
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Contoh: Posisi bermain"
            className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
          />
        </div>

        {/* Answer Type Selection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold tracking-wider text-gray-500 uppercase">
              TIPE JAWABAN
            </label>
            {templateType === 'player_number' && (
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                Terkunci angka otomatis
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {typePills.map((pill) => {
              const isSelected = type === pill.type;
              const isLockedOther = templateType === 'player_number' && pill.type !== 'number';
              return (
                <button
                  key={pill.type}
                  type="button"
                  disabled={isLockedOther}
                  onClick={() => !isLockedOther && handleTypeChange(pill.type)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                    isLockedOther
                      ? 'opacity-40 bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      : isSelected
                      ? 'border-[#FC480D] bg-orange-50/70 text-[#FC480D] shadow-2xs font-bold cursor-pointer'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50/50 cursor-pointer'
                  }`}
                >
                  <Icon icon={pill.icon} className="w-4 h-4" />
                  <span>{pill.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Info banner for Player Number template */}
        {templateType === 'player_number' && (
          <div className="p-3 bg-emerald-50/80 border border-emerald-200 rounded-xl text-xs text-emerald-900 leading-relaxed flex items-start gap-2">
            <Icon
              icon="ph:info-bold"
              className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"
            />
            <span>
              Nomor punggung bernilai angka dan akan terintegrasi langsung saat membuat tim & lineup game pertandingan.
            </span>
          </div>
        )}

        {/* Options List if Type === 'option' */}
        {type === 'option' && (
          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold tracking-wider text-gray-500 uppercase">
                OPSI JAWABAN
              </label>
              {(templateType === 'form_role' ||
                templateType === 'role' ||
                templateType === BADMINTON_LEVEL_TEMPLATE) && (
                <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                  Terkunci otomatis
                </span>
              )}
            </div>

            {readOnlyOptions ? (
              <div className="space-y-2">
                <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed flex items-start gap-2">
                  <Icon
                    icon="ph:info-bold"
                    className="w-4 h-4 text-amber-600 shrink-0 mt-0.5"
                  />
                  <span>
                    {templateType === BADMINTON_LEVEL_TEMPLATE
                      ? 'Opsi level baku dan dipakai untuk menyeimbangkan tim saat generate lineup otomatis.'
                      : 'Opsi posisi pemain baku dan terhubung langsung ke formasi lineup pertandingan (GK, DF, MF, FW).'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {options.map((opt, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700 flex items-center justify-between"
                    >
                      <span>{opt}</span>
                      <Icon icon="ph:lock-simple-bold" className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                {options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-5 text-center text-xs font-semibold text-gray-400">
                      {i + 1}.
                    </span>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => handleOptionChange(i, e.target.value)}
                      placeholder={`Opsi ${i + 1}`}
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D]"
                    />
                    {options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(i)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Hapus opsi"
                      >
                        <Icon icon="ph:trash-bold" className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddOption}
                  className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-[#FC480D] hover:text-[#d83c08] transition-colors py-1 cursor-pointer"
                >
                  <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
                  <span>Tambah Opsi</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Required Switch Toggle */}
        <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
          <div>
            <span className="block text-sm font-bold text-gray-900">Wajib diisi</span>
            <span className="block text-xs text-gray-500">
              Peserta tidak bisa lanjut tanpa mengisi pertanyaan ini
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={required}
            onClick={() => setRequired(!required)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              required ? 'bg-[#FC480D]' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                required ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Action Button */}
        <div className="pt-2">
          <Button
            type="button"
            onClick={handleSave}
            className="w-full bg-[#FC480D] hover:bg-[#d83c08] text-white font-bold py-3 rounded-xl shadow-xs"
          >
            Simpan Pertanyaan
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomFormModal;
